import { ChangeDetectionStrategy, Component, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatterRequisitionPrecedent } from '@models/requisitions';
import { Observable, combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { IRequisitionTextAreaModel } from '../requisition-text-areas/requisition-text-areas.component';
import { RequisitionService } from '@services/requisition.service';
import { TestHooks } from './requisition-text-column.hooks';

@Component({
	selector: 'app-requisition-text-column',
	templateUrl: './requisition-text-column.component.html',
	styleUrls: ['./requisition-text-column.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => RequisitionTextColumnComponent),
			multi: true,
		},
	],
	standalone: false,
})
export class RequisitionTextColumnComponent implements ControlValueAccessor {
	testHooks = TestHooks;
	public isDisabled = false;
	public initialRequisitionTexts = { text: '', requisitionText: '' };
	public viewOnly = true;
	public items = [
		{ value: true, label: 'Preview' },
		{ value: false, label: 'Edit View' },
	];

	public parsedTexts = { text: '', requisitionText: '' };
	public value: MatterRequisitionPrecedent;

	public get texts(): IRequisitionTextAreaModel {
		return this.viewOnly ? this.parsedTexts : this.value;
	}

	public set texts(value: IRequisitionTextAreaModel) {
		if (this.viewOnly) return;
		this.value = { ...this.value, ...value };
		this.onChange(this.value);
	}

	constructor(private requisitionService: RequisitionService) {}

	onChange: (value: MatterRequisitionPrecedent) => void;
	onTouched: () => void;

	writeValue(obj: MatterRequisitionPrecedent): void {
		if (obj) {
			if (obj.id !== this.value?.id) {
				this.initialRequisitionTexts = {
					text: obj.text,
					requisitionText: obj.requisitionText,
				};
			}
			this.value = { ...this.value, ...obj };
			this.updateParsedDoc();
		}
	}
	registerOnChange(fn: (value: MatterRequisitionPrecedent) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: VoidFunction): void {
		this.onTouched = fn;
	}

	updateParsedDoc(): void {
		if (!this.viewOnly) return;

		combineLatest([
			this.getPopulatedText(this.value?.text),
			this.getPopulatedText(this.value?.requisitionText),
		])
			.pipe(take(1))
			.subscribe(([parsedText, parsedRequiredText]) => {
				this.parsedTexts = {
					text: parsedText,
					requisitionText: parsedRequiredText,
				};
			});
	}

	/**
	 *
	 * @param isDisabled boolean value to set the disabled state of the component
	 * @returns void
	 * @description sets the disabled state of the component
	 * @default isDisabled = false
	 * Adds the ability to disable the text areas until a requisition has been selected.
	 */

	setDisabledState(isDisabled: boolean): void {
		this.isDisabled = isDisabled;
	}

	private getPopulatedText(text: string): Observable<string> {
		return this.requisitionService
			.getPopulatedText(text, this.value?.fields)
			.pipe(take(1));
	}
}
